import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CIP_APP_URL, CIP_RSMIN_TENANT_SLUG } from '../components/config';
import './CipEmbedPage.css';

/**
 * CipEmbedPage — Admin-only page for accessing the CIP (Civic Intelligence Platform)
 * via the rsmin-callback token bridge, embedded as an iframe or launched in a new tab.
 */
const CipEmbedPage = () => {
  const navigate = useNavigate();
  const [embedMode, setEmbedMode] = useState('iframe'); // 'iframe' | 'tab'
  const [cipPath, setCipPath] = useState(`/t/${CIP_RSMIN_TENANT_SLUG}`);
  const [bridgeUrl, setBridgeUrl] = useState('');
  const [error, setError] = useState('');

  const authToken = localStorage.getItem('authToken');
  const cipReady = !!CIP_APP_URL;
  const cipBase = CIP_APP_URL ? CIP_APP_URL.replace(/\/$/, '') : '';

  const CIP_PATHS = [
    { label: 'CIP Home', value: `/t/${CIP_RSMIN_TENANT_SLUG}` },
    { label: 'Harvesting Module', value: `/t/${CIP_RSMIN_TENANT_SLUG}/harvesting` },
    { label: 'Consultation Module', value: `/t/${CIP_RSMIN_TENANT_SLUG}/consultation` },
    { label: 'Community Module', value: `/t/${CIP_RSMIN_TENANT_SLUG}/community` },
  ];

  useEffect(() => {
    if (!cipReady) {
      setError('CIP integration is not configured. Set REACT_APP_CIP_APP_URL in the environment.');
      return;
    }
    if (!authToken) {
      setError('No authentication token found. Please log in again.');
      return;
    }

    const returnUrl = `${window.location.origin}/admin/cip-embed`;
    const next = `${cipPath}?embedded=1&rsminReturn=${encodeURIComponent(returnUrl)}&rsminLabel=${encodeURIComponent('\u2190 Back to Admin')}`;
    const url = `${cipBase}/auth/rsmin-callback?token=${encodeURIComponent(authToken)}&next=${encodeURIComponent(next)}`;
    setBridgeUrl(url);
    setError('');
  }, [cipReady, authToken, cipBase, cipPath]);

  const handleOpenInTab = () => {
    if (bridgeUrl) {
      window.open(bridgeUrl, '_blank', 'noreferrer');
    }
  };

  return (
    <div className="cip-embed-page">
      <div className="cip-embed-header">
        <div className="cip-embed-header-left">
          <h1>🏛️ CIP Platform</h1>
          <p>Civic Intelligence Platform — RSMIN Tenant</p>
        </div>
        <div className="cip-embed-header-right">
          <Link to="/admin-dashboard" className="btn-back-admin">
            ← Admin Dashboard
          </Link>
        </div>
      </div>

      {error && (
        <div className="cip-embed-error">
          <p>{error}</p>
          {!authToken && (
            <Link to="/login" className="btn-login">
              Go to Login
            </Link>
          )}
        </div>
      )}

      {!error && (
        <>
          <div className="cip-embed-controls">
            <div className="cip-path-selector">
              <label htmlFor="cip-path-select">CIP Module:</label>
              <select
                id="cip-path-select"
                value={cipPath}
                onChange={(e) => setCipPath(e.target.value)}
                className="cip-path-select"
              >
                {CIP_PATHS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="cip-view-toggle">
              <button
                className={`btn-view-mode ${embedMode === 'iframe' ? 'active' : ''}`}
                onClick={() => setEmbedMode('iframe')}
              >
                Embedded View
              </button>
              <button
                className="btn-view-mode"
                onClick={handleOpenInTab}
              >
                Open in New Tab ↗
              </button>
            </div>
          </div>

          {embedMode === 'iframe' && bridgeUrl && (
            <div className="cip-iframe-container">
              <iframe
                key={bridgeUrl}
                title="Civic Intelligence Platform"
                src={bridgeUrl}
                className="cip-iframe"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
              />
            </div>
          )}
        </>
      )}

      <div className="cip-embed-footer">
        <p>
          CIP integration is managed via the{' '}
          <Link to="/admin/connector-settings">Integrations Settings</Link>. RS-Education
          users were migrated to CIP and can be managed from within the CIP admin panel.
        </p>
      </div>
    </div>
  );
};

export default CipEmbedPage;
